import * as React from 'react';
import moment from 'moment';

import ActivityDetails from './activity_details';
import KeyTargetSkillConcepts from './key_target_skill_concepts';
import ActivityDetailsSection from './activity_details_section';

import { getTimeSpent } from '../../../helpers/studentReports';
import numberSuffixBuilder from '../../modules/numberSuffixBuilder';
import PercentageDisplayer from '../../modules/percentage_displayer.jsx';
import { proficiencyCutoffsAsPercentage } from '../../../../../modules/proficiency_cutoffs';
import { NOT_APPLICABLE, safeParseHtml, Spinner, EVIDENCE, } from '../../../../Shared'
import useWindowSize from '../../../../Shared/hooks/useWindowSize'

const ORDINAL_NUMBERS = ['Zeroth', 'First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth']
const QUILL_DIAGNOSTIC_SCORING_EXPLANATION = "The Quill Diagnostic is meant to diagnose skills to practice. Students are not provided a color-coded score or percentage score. Teachers see only a percentage score without a color."
const percentageDisplayer = new PercentageDisplayer()

interface Session {
  grouped_key_target_skill_concepts: any;
  percentage: number;
  number_of_correct_questions: number;
  number_of_questions: number;
  completed_at: string;
  timespent: number;
}

interface ActivityClassification {
  id: number;
}

interface Activity {
  classification: ActivityClassification;
  name: string;
}

interface ScorebookTooltipData {
  completed_attempts: Number;
  name: string; // this is the activity name
  activity_classification_id: number;
  percentage: number;
  activity?: Activity;
  started?: number;
  sessions?: Array<Session>
  locked?: Boolean;
  scheduled?: Boolean;
  marked_complete?: Boolean;
  activity_classification_key?: string;
}

interface ScorebookTooltipProps {
  data: ScorebookTooltipData
  inStudentView?: Boolean;
  showExactScores?: Boolean;
  id: string;
}

export const ScorebookTooltip = ({ data, inStudentView, showExactScores, id, }: ScorebookTooltipProps) => {
  const tooltipRef = React.useRef(null);
  const [tooltipStyle, setTooltipStyle] = React.useState({});
  const [caretStyle, setCaretStyle] = React.useState({});
  const size = useWindowSize()

  const { marked_complete, completed_attempts, locked, scheduled, sessions, started, activity, name, activity_classification_id } = data
  const actClassId = activity ? activity.classification.id : activity_classification_id;
  const isDiagnostic = Number(actClassId) === 4

  if (!Object.keys(data).length) { return <span /> }

  React.useEffect(() => {
    // this hook makes sure the tooltip doesn't end up off the screen to the left or the right, and repositions the caret if the tooltip itself has moved

    if (!tooltipRef.current) { return }

    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = size.width;

    let { right, left } = tooltipRect;

    if (left < 0) {
      setTooltipStyle({
        left: '0px',
        right: 'unset'
      });

      setCaretStyle({
        left: '16px',
        right: 'unset'
      })
    } else if (right > viewportWidth) {
      setTooltipStyle({
        left: 'unset',
        right: '0px',
      });

      setCaretStyle({
        left: 'unset',
        right: '16px'
      })
    }
  }, [size]);


  function activityOverview() {
    return (
      <div className="activity-overview">
        <ActivityDetails data={data} />
        {totalScoreOrNot()}
        {scoringExplanation()}
      </div>
    )
  }

  function keyTargetSkillConceptsOrExplanation() {
    if (marked_complete && completed_attempts === 0) {
      return <p className="no-data-message">This student has missed this lesson. To make up this material, you can assign this lesson again to the students who missed it.</p>
    } else if (scheduled && !completed_attempts) {
      return <p className="no-data-message">This scheduled activity has not been published.</p>
    } else if (locked) {
      return <p className="no-data-message">This activity is set for staggered release and has not been unlocked by this student.</p>
    } else if (!completed_attempts) {
      return <p className="no-data-message">This activity has not been completed.</p>
    } else if (sessions && sessions.length) {
      return <KeyTargetSkillConcepts groupedKeyTargetSkillConcepts={sessions[sessions.length - 1].grouped_key_target_skill_concepts} shouldShowCounts={!inStudentView || showExactScores} />
    } else {
      return <Spinner />
    }
  };

  function displayScores() {
    const attemptInProgress = started > 0

    return sessions.map((session, i) => {
      const { percentage, number_of_correct_questions, number_of_questions, completed_at, timespent, is_final_score, } = session
      const ordinalNumber = numberSuffixBuilder(i + 1)
      const formattedPercentage = percentageDisplayer.run(percentage)
      const percentageForDisplay = isDiagnostic ? '' : ` (${formattedPercentage})`
      const scoreText = `${number_of_correct_questions} of ${number_of_questions} Target Skills Correct${percentageForDisplay}`
      const sessionLength = sessions.length
      let attemptText = ''

      if (attemptInProgress && i === sessionLength - 1) {
        const nextNumber = sessionLength + 1
        const textifiedNextNumber = nextNumber > 10 ? numberSuffixBuilder(i + 1) : ORDINAL_NUMBERS[nextNumber]
        attemptText = `(${textifiedNextNumber} attempt in progress)`
        const descriptionElement = <p className="description"><span className="percentage">{scoreText}</span><br /> {attemptText}</p>
        return <ActivityDetailsSection description={descriptionElement} header={`${ordinalNumber} score`} key={i} />
      }
      const descriptionElement = (
        <div className="description-block">
          <p className="description"><span className="percentage">{scoreText}</span> {attemptText}</p>
          <p className="description">{`${moment.utc(completed_at).format('MMMM D, YYYY [at] h:mm a')} / ${timespent ? getTimeSpent(timespent) : NOT_APPLICABLE}`}</p>
        </div>
      )
      return <ActivityDetailsSection description={descriptionElement} header={`${ordinalNumber} score${inStudentView && is_final_score ? '*' : ''}`} key={i} />
    })
  };

  function totalScoreOrNot() {
    const { percentage, sessions, } = data

    const hasScoreData = percentage !== null && sessions && sessions.length > 0
    if (hasScoreData && (!inStudentView || showExactScores)) {
      return displayScores()
    } else {
      return <span />
    }
  };

  function scoringExplanation() {
    if (!isDiagnostic) { return } 
    
    return <ActivityDetailsSection description={QUILL_DIAGNOSTIC_SCORING_EXPLANATION} header="Scoring" />
  }

  function colorExplanation() {
    const { percentage, activity_classification_key, } = data

    if (percentage === null) { return }

    const cutOff = proficiencyCutoffsAsPercentage();

    const scoreForComparison = percentage * 100

    const isEvidenceActivity = activity_classification_key === EVIDENCE

    let header = 'Why Red?'
    let descriptionElements = isEvidenceActivity ?
      <p>You did not write a strong response within five revisions on any of the prompts.</p> :
      (
        <React.Fragment>
          <p className="description">Rarely Demonstrated Skill</p>
          <p className="description">{cutOff.nearlyProficient - 1} - 0% of prompts exhibit skill</p>
        </React.Fragment>
      )

    if (scoreForComparison >= cutOff.proficient) {
      header = 'Why Green?'
      descriptionElements = isEvidenceActivity ?
        <p>Within five revisions, you wrote a strong response for all prompts.</p> :
        (
          <React.Fragment>
            <p className="description">Frequently Demonstrated Skill</p>
            <p className="description">100 - {cutOff.proficient}% of prompts exhibit skill</p>
          </React.Fragment>
        )
    } else if (scoreForComparison >= cutOff.nearlyProficient) {
      header = 'Why Yellow?'
      descriptionElements = isEvidenceActivity ?
        <p>Within five revisions, you wrote a strong response for some, but not all, prompts.</p> :
        (
          <React.Fragment>
            <p className="description">Sometimes Demonstrated Skill</p>
            <p className="description">{cutOff.proficient - 1} - {cutOff.nearlyProficient}% of prompts exhibit skill</p>
          </React.Fragment>
        )
    }

    const descriptionElement = (
      <div className="description-block">
        {descriptionElements}
      </div>
    )
    return <ActivityDetailsSection description={descriptionElement} header={header} />
  }

  function tooltipMessage() {
    if (inStudentView && !showExactScores) { return }

    let text = 'Clicking on the activity icon loads the report'

    if (inStudentView && showExactScores) {
      text = '*Your dashboard shows the highest score of all your attempts'
    }

    return (
      <p className="tooltip-message">{text}</p>
    )
  }

  const title = activity ? activity.name : name;
  return (
    <div className="scorebook-tooltip" id={id} ref={tooltipRef} role="tooltip" style={tooltipStyle}>
      <i className="fas fa-caret-up" style={caretStyle} />
      <i className="fas fa-caret-up border-color" style={caretStyle} />
      <div className="title">
        {safeParseHtml(title)}
      </div>
      <div className="main">
        {activityOverview()}
        {keyTargetSkillConceptsOrExplanation()}
        {inStudentView ? colorExplanation() : null}
        {tooltipMessage()}
      </div>
    </div>
  )
}

export default ScorebookTooltip
